import React, { useEffect, useMemo, useRef, useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import { Box, CircularProgress, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import DataTable from "../../../Components/Common/datatable/DataTable";
import { MessagePopup } from "../../../Components/Common/messagePopup/MessagePopup";
import {
  bulkToggleUserMappings,
  clearProfitCenterMessage,
  clearUserMappings,
  fetchProfitCenterOptions,
  fetchProfitCenterUsers,
  fetchUserMappings,
  saveUserMappings,
} from "../../../redux/profitCenterConfigurationSlice";
import ProfitCenterAssignmentEditor from "./ProfitCenterAssignmentEditor";

const ProfitCenterConfiguration = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const { users, profitCenterOptions, userMappings, loading, loadingMappings, saving, error, successMessage } =
    useSelector((state) => state.profitCenterConfiguration);

  useEffect(() => {
    dispatch(fetchProfitCenterUsers());
    dispatch(fetchProfitCenterOptions());
  }, [dispatch]);

  useEffect(() => {
    if (!successMessage) {
      return;
    }

    MessagePopup({
      icon: "success",
      title: "Success",
      message: successMessage,
      confirmButtonText: "Ok",
      confirmButtonColor: "#000000",
    });
    dispatch(clearProfitCenterMessage());
  }, [successMessage, dispatch]);

  useEffect(() => {
    if (!error) {
      return;
    }

    MessagePopup({
      icon: "error",
      title: "Error",
      message: error,
      confirmButtonText: "Ok",
      confirmButtonColor: "#000000",
    });
    dispatch(clearProfitCenterMessage());
  }, [error, dispatch]);

  const rows = useMemo(() => {
    return users.map((user) => ({
      ...user,
      userName: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
    }));
  }, [users]);

  const selectedUser = useMemo(
    () => users.find((user) => user.id === selectedUserId),
    [users, selectedUserId],
  );

  const onGridReady = (params) => {
    if (params.api) {
      params.api.sizeColumnsToFit();
    }
  };

  const handleEdit = (userId) => {
    setSelectedUserId(userId);
    dispatch(fetchUserMappings(userId));
  };

  const handleBack = () => {
    setSelectedUserId(null);
    dispatch(clearUserMappings());
  };

  const handleSave = (mappings) => {
    if (!selectedUser) {
      return;
    }

    dispatch(
      saveUserMappings({
        userId: selectedUser.id,
        mappings,
      }),
    );
  };

  const handleBulkToggle = ({ mappingIds, isEnabled }) => {
    dispatch(bulkToggleUserMappings({ mappingIds, isEnabled }));
  };

  const columns = useMemo(
    () => [
      {
        field: "userName",
        headerName: "User Name",
        sortable: true,
        unSortIcon: true,
        width: 220,
        minWidth: 220,
        cellRenderer: (params) => (
          <Tooltip title={params.data.userName} arrow>
            <span>{params.data.userName}</span>
          </Tooltip>
        ),
      },
      {
        field: "emailId",
        headerName: "Email",
        sortable: true,
        unSortIcon: true,
        width: 260,
        minWidth: 260,
        cellRenderer: (params) => (
          <Tooltip title={params.data.emailId} arrow>
            <span>{params.data.emailId}</span>
          </Tooltip>
        ),
      },
      {
        field: "userRole",
        headerName: "Role",
        sortable: true,
        unSortIcon: true,
        width: 180,
        minWidth: 180,
      },
      {
        field: "actions",
        headerName: "Action",
        width: 120,
        minWidth: 120,
        sortable: false,
        cellRenderer: (params) => (
          <EditIcon
            onClick={() => handleEdit(params.data.id)}
            style={{ cursor: "pointer" }}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  if (selectedUser) {
    return (
      <ProfitCenterAssignmentEditor
        user={selectedUser}
        profitCenterOptions={profitCenterOptions}
        userMappings={userMappings}
        loadingMappings={loadingMappings}
        saving={saving}
        onBack={handleBack}
        onSave={handleSave}
        onBulkToggle={handleBulkToggle}
      />
    );
  }

  return (
    <Box>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "70vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <DataTable
          ref={gridRef}
          columns={columns}
          rows={rows}
          tableHeight={"70vh"}
          suppressMovableColumns={true}
          suppressHorizontalScroll={false}
          suppressPaginationPanel={false}
          pagination={true}
          paginationPageSize={10}
          paginationPageSizeSelector={[5, 10, 20, 50, 100]}
          onGridReady={onGridReady}
        />
      )}
    </Box>
  );
};

export default ProfitCenterConfiguration;